import {
  fetchChat,
  markMessagesAsRead,
  postMessage,
  postShareProfile,
  postSharedExperience,
  postSharedLocation,
  rejectUserRequest as rejectUserRequestCall,
  requestReschedule as requestRescheduleCall,
} from 'flux/clients/messages2';

import ActionTypes from './ActionTypes';

export interface Actions {
  fetchMessages: (options: {
    contactId: string;
    from: number;
    size: number;
  }) => void;
  sendMessage: (options: { message: string; to: string; from: string }) => void;
  markConversationAsRead: (options: {
    accountId: string;
    contactId: string;
  }) => void;
  markOwnConversationAsRead: (options: {
    accountId: string;
    contactId: string;
  }) => void;
  shareProfile: (options: { to: string; hostId: string; from: string }) => void;
  shareExperience: (options: {
    to: string;
    experienceId: string;
    from: string;
  }) => void;
  shareLocation: (options: {
    to: string;
    location: { lat: number; lng: number };
    from: string;
  }) => void;
  rejectUserRequest: (options: { requestId: string }) => Promise<void>;
  requestReschedule: (options: { bookingId: string; message: string }) => void;
}

export default function actionsFactory(
  dispatch: (action: ActionTypes) => void,
): Actions {
  return {
    fetchMessages: ({ contactId, from, size }) => {
      dispatch({ type: 'MESSAGES:FETCHING', payload: { contactId } });

      fetchChat({ contactId, from, size })
        .then((result) =>
          dispatch({
            type: 'MESSAGES:FETCHING_SUCCESS',
            payload: { contactId, result },
          }),
        )
        .catch((error) =>
          dispatch({
            type: 'MESSAGES:FETCHING_ERROR',
            payload: { contactId, error },
          }),
        );
    },
    sendMessage: ({ message, to, from }) => {
      const tempId = `${Math.random()}`;

      dispatch({
        type: 'MESSAGES:SEND_MESSAGE',
        payload: {
          type: 'ChatMessage',
          message: {
            to,
            from,
            body: message,
            id: tempId,
            created_at: new Date().getTime(),
            isCensored: false,
            status: 'READ',
          },
          isSending: true,
        },
      });

      postMessage({ message, to })
        .then((result) =>
          dispatch({
            type: 'MESSAGES:SEND_MESSAGE_SUCCESS',
            payload: { type: 'ChatMessage', message: result, tempId },
          }),
        )
        .catch((error) =>
          dispatch({
            type: 'MESSAGES:SEND_MESSAGE_ERROR',
            payload: { to, tempId, error },
          }),
        );
    },
    markConversationAsRead: ({ accountId, contactId }) => {
      dispatch({
        type: 'MESSAGES:MARK_AS_READ',
        payload: { accountId, contactId },
      });
      markMessagesAsRead({ contactId });
    },
    markOwnConversationAsRead: ({ accountId, contactId }) => {
      dispatch({
        type: 'MESSAGES:MARK_OWN_AS_READ',
        payload: { accountId, contactId },
      });
    },
    shareProfile: ({ to, hostId, from }) => {
      const tempId = `${Math.random()}`;

      dispatch({
        type: 'MESSAGES:SEND_MESSAGE',
        payload: {
          type: 'SharedProfile',
          message: {
            to,
            from,
            body: '',
            id: tempId,
            type: 'SharedProfile',
            created_at: new Date().getTime(),
            status: 'READ',
            profile_id: hostId,
          },
          isSending: true,
        },
      });

      postShareProfile({ to, account_id: hostId })
        .then((result) =>
          dispatch({
            type: 'MESSAGES:SEND_MESSAGE_SUCCESS',
            payload: { type: 'SharedProfile', message: result, tempId },
          }),
        )
        .catch((error) =>
          dispatch({
            type: 'MESSAGES:SEND_MESSAGE_ERROR',
            payload: { to, tempId, error },
          }),
        );
    },
    shareExperience: ({ to, experienceId, from }) => {
      const tempId = `${Math.random()}`;

      dispatch({
        type: 'MESSAGES:SEND_MESSAGE',
        payload: {
          type: 'SharedExperience',
          message: {
            to,
            from,
            experience_id: experienceId,
            body: '',
            id: tempId,
            type: 'SharedExperience',
            created_at: new Date().getTime(),
            status: 'READ',
          },
          isSending: true,
        },
      });

      postSharedExperience({ to, experienceId })
        .then((result) =>
          dispatch({
            type: 'MESSAGES:SEND_MESSAGE_SUCCESS',
            payload: { type: 'SharedExperience', message: result, tempId },
          }),
        )
        .catch((error) =>
          dispatch({
            type: 'MESSAGES:SEND_MESSAGE_ERROR',
            payload: { to, tempId, error },
          }),
        );
    },
    shareLocation: ({ to, location, from }) => {
      const tempId = `${Math.random()}`;

      dispatch({
        type: 'MESSAGES:SEND_MESSAGE',
        payload: {
          type: 'SharedLocation',
          message: {
            to,
            from,
            location,
            type: 'SharedLocation',
            id: tempId,
            created_at: new Date().getTime(),
            status: 'READ',
          },
          isSending: true,
        },
      });
      postSharedLocation({ to, location })
        .then((result) =>
          dispatch({
            type: 'MESSAGES:SEND_MESSAGE_SUCCESS',
            payload: { type: 'SharedLocation', message: result, tempId },
          }),
        )
        .catch((error) =>
          dispatch({
            type: 'MESSAGES:SEND_MESSAGE_ERROR',
            payload: { to, tempId, error },
          }),
        );
    },
    rejectUserRequest: ({ requestId }) => {
      return rejectUserRequestCall({ requestId }).then((result) =>
        dispatch({ type: 'MESSAGES:NEW_MESSAGE', payload: result }),
      );
    },
    requestReschedule: ({ message, bookingId }) => {
      requestRescheduleCall({ message, bookingId }).then((result) =>
        dispatch({
          type: 'MESSAGES:SEND_MESSAGE_SUCCESS',
          payload: { type: 'RescheduleRequest', message: result },
        }),
      );
    },
  };
}
